import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { Tabs, TabsContent } from '@/components/ui-kit/tabs';
import {
  ContractsTabsList,
  type ContractsTabsListOption,
} from '@/modules/org/contracts/components/contracts-tabs-list';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Bot,
  CheckCircle2,
  FileText,
  Globe,
  KeyRound,
  LayoutGrid,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Trash2,
  Workflow,
  X,
} from 'lucide-react';

import { useGetSettingsMap, useUpsertSetting } from '@/modules/admin/hooks/use-admin';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { ModelSelector } from '../../analytics/components/model-selector';
import {
  DEFAULT_COMMON_PROMPT_INSTRUCTIONS,
  DEFAULT_DYNAMIC_FORM_SYSTEM_PROMPT,
  DEFAULT_TEMPLATE_CONFIGURATOR_BUSINESS_LOGIC,
} from '../utils';

const DEFAULT_DYNAMIC_AI_MODEL = 'meta-llama/llama-4-scout:nitro';
const DEFAULT_DOCUMENT_GENERATION_MODEL = 'anthropic/claude-3.5-sonnet';
const DEFAULT_FORM_ENRICHMENT_MODEL = 'meta-llama/llama-4-scout:nitro';
const DEFAULT_TEMPLATE_CONFIGURATOR_MODEL = 'anthropic/claude-sonnet-4';

const settingsSchema = z.object({
  // Document generation settings
  documentGenerationAiModel: z.string().optional(),
  commonPromptInstructions: z.string().optional(),
  documentGenerationOutputInUserLocale: z.boolean().default(false),
  // Dynamic form AI settings
  dynamicFormAiModel: z.string().optional(),
  dynamicFormSystemPrompt: z.string().optional(),
  dynamicFormOutputInUserLocale: z.boolean().default(false),
  // Form enrichment AI settings
  formEnrichmentAiModel: z.string().optional(),
  formEnrichmentOutputInUserLocale: z.boolean().default(false),
  // Template AI Configurator settings
  templateConfiguratorAiModel: z.string().optional(),
  templateConfiguratorBusinessLogic: z.string().optional(),
  // Integration keys
  blocksProjectKey: z.string().optional(),
  openRouterApiKey: z.string().optional(),
  seliseClientId: z.string().optional(),
  seliseClientSecret: z.string().optional(),
});
type SettingsFormInput = z.input<typeof settingsSchema>;

type SettingsTabValue = 'integration' | 'document' | 'enrichment' | 'dynamic' | 'configurator';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<SettingsTabValue>('integration');

  // Track which credentials are configured (from API)
  const [configuredCredentials, setConfiguredCredentials] = useState({
    blocksProjectKey: false,
    openRouterApiKey: false,
    seliseCredentials: false,
  });

  // Track which credential is being edited
  const [editingCredential, setEditingCredential] = useState<string | null>(null);

  // Track credential being removed (for confirmation dialog)
  const [removingCredential, setRemovingCredential] = useState<string | null>(null);

  // Temporary values for credential editing
  const [credentialValues, setCredentialValues] = useState({
    blocksProjectKey: '',
    openRouterApiKey: '',
    seliseClientId: '',
    seliseClientSecret: '',
  });

  const form = useForm<SettingsFormInput>({
    resolver: zodResolver(settingsSchema),
    defaultValues: {
      documentGenerationAiModel: DEFAULT_DOCUMENT_GENERATION_MODEL,
      commonPromptInstructions: '',
      documentGenerationOutputInUserLocale: false,
      dynamicFormAiModel: DEFAULT_DYNAMIC_AI_MODEL,
      dynamicFormSystemPrompt: '',
      dynamicFormOutputInUserLocale: false,
      formEnrichmentAiModel: DEFAULT_FORM_ENRICHMENT_MODEL,
      formEnrichmentOutputInUserLocale: false,
      templateConfiguratorAiModel: DEFAULT_TEMPLATE_CONFIGURATOR_MODEL,
      templateConfiguratorBusinessLogic: '',
      blocksProjectKey: '',
      openRouterApiKey: '',
      seliseClientId: '',
      seliseClientSecret: '',
    },
  });

  // Fetch all settings as key→value map from UDS
  const { data: settingsMap, isLoading: settingsLoading } = useGetSettingsMap();
  const { mutateAsync: upsertSettingMutation } = useUpsertSetting();

  useEffect(() => {
    if (settingsMap) {
      const data = settingsMap;
      form.reset({
        documentGenerationAiModel:
          data.documentGenerationAiModel || DEFAULT_DOCUMENT_GENERATION_MODEL,
        commonPromptInstructions:
          data.commonPromptInstructions || DEFAULT_COMMON_PROMPT_INSTRUCTIONS,
        documentGenerationOutputInUserLocale: data.documentGenerationOutputInUserLocale === 'true',
        dynamicFormAiModel: data.dynamicFormAiModel || DEFAULT_DYNAMIC_AI_MODEL,
        dynamicFormSystemPrompt: data.dynamicFormSystemPrompt || DEFAULT_DYNAMIC_FORM_SYSTEM_PROMPT,
        dynamicFormOutputInUserLocale: data.dynamicFormOutputInUserLocale === 'true',
        formEnrichmentAiModel: data.formEnrichmentAiModel || DEFAULT_FORM_ENRICHMENT_MODEL,
        formEnrichmentOutputInUserLocale: data.formEnrichmentOutputInUserLocale === 'true',
        templateConfiguratorAiModel:
          data.templateConfiguratorAiModel || DEFAULT_TEMPLATE_CONFIGURATOR_MODEL,
        templateConfiguratorBusinessLogic:
          data.templateConfiguratorBusinessLogic || DEFAULT_TEMPLATE_CONFIGURATOR_BUSINESS_LOGIC,
        blocksProjectKey: '',
        openRouterApiKey: '',
        seliseClientId: '',
        seliseClientSecret: '',
      });

      setConfiguredCredentials({
        blocksProjectKey: !!data.blocksProjectKey,
        openRouterApiKey: !!data.openRouterApiKey,
        seliseCredentials: !!data.seliseClientId && !!data.seliseClientSecret,
      });
      setIsLoading(false);
    } else if (!settingsLoading) {
      setIsLoading(false);
    }
  }, [settingsMap, settingsLoading, form]);

  const handleSubmit = async (data: SettingsFormInput) => {
    setIsSaving(true);
    try {
      // Save each non-credential setting as a key→value pair in UDS
      const settingsToSave: Record<string, string> = {
        documentGenerationAiModel: data.documentGenerationAiModel || '',
        commonPromptInstructions: data.commonPromptInstructions || '',
        documentGenerationOutputInUserLocale: String(
          data.documentGenerationOutputInUserLocale ?? false
        ),
        dynamicFormAiModel: data.dynamicFormAiModel || '',
        dynamicFormSystemPrompt: data.dynamicFormSystemPrompt || '',
        dynamicFormOutputInUserLocale: String(data.dynamicFormOutputInUserLocale ?? false),
        formEnrichmentAiModel: data.formEnrichmentAiModel || '',
        formEnrichmentOutputInUserLocale: String(data.formEnrichmentOutputInUserLocale ?? false),
        templateConfiguratorAiModel: data.templateConfiguratorAiModel || '',
        templateConfiguratorBusinessLogic: data.templateConfiguratorBusinessLogic || '',
      };

      await Promise.all(
        Object.entries(settingsToSave).map(([key, value]) => upsertSettingMutation({ key, value }))
      );

      toast({
        variant: 'success',
        title: 'Success',
        description: t('ADMIN_SETTINGS_TOASTS_SETTINGSSAVED'),
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          error instanceof Error ? error.message : t('ADMIN_SETTINGS_ERRORS_SAVESETTINGS'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetDocumentGeneration = () => {
    form.setValue('documentGenerationAiModel', DEFAULT_DOCUMENT_GENERATION_MODEL);
    form.setValue('commonPromptInstructions', DEFAULT_COMMON_PROMPT_INSTRUCTIONS);
    toast({
      variant: 'info',
      title: 'Info',
      description: t('ADMIN_SETTINGS_TOASTS_RESETDOCUMENT'),
    });
  };

  const handleResetDynamicForm = () => {
    form.setValue('dynamicFormAiModel', DEFAULT_DYNAMIC_AI_MODEL);
    form.setValue('dynamicFormSystemPrompt', DEFAULT_DYNAMIC_FORM_SYSTEM_PROMPT);
    toast({ variant: 'info', title: 'Info', description: t('ADMIN_SETTINGS_TOASTS_RESETDYNAMIC') });
  };

  const handleResetTemplateConfigurator = () => {
    form.setValue('templateConfiguratorAiModel', DEFAULT_TEMPLATE_CONFIGURATOR_MODEL);
    form.setValue(
      'templateConfiguratorBusinessLogic',
      DEFAULT_TEMPLATE_CONFIGURATOR_BUSINESS_LOGIC
    );
    toast({
      variant: 'info',
      title: 'Info',
      description: t('ADMIN_SETTINGS_TOASTS_RESETTEMPLATECONFIGURATOR'),
    });
  };

  // Credential management handlers
  const handleSaveCredential = async (key: string) => {
    // For SELISE credentials, handle as a pair
    if (key === 'seliseCredentials') {
      const clientId = credentialValues.seliseClientId;
      const clientSecret = credentialValues.seliseClientSecret;

      if (!clientId || !clientSecret) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: t('ADMIN_SETTINGS_ERRORS_MISSINGSELISECREDENTIALS'),
        });
        return;
      }

      setIsSaving(true);
      try {
        await Promise.all([
          upsertSettingMutation({ key: 'seliseClientId', value: clientId }),
          upsertSettingMutation({ key: 'seliseClientSecret', value: clientSecret }),
        ]);

        setConfiguredCredentials((prev) => ({
          ...prev,
          seliseCredentials: true,
        }));
        setEditingCredential(null);
        setCredentialValues((prev) => ({
          ...prev,
          seliseClientId: '',
          seliseClientSecret: '',
        }));
        toast({
          variant: 'success',
          title: 'Success',
          description: t('ADMIN_SETTINGS_TOASTS_SELISECREDENTIALSSAVED'),
        });
      } catch (error) {
        console.error('Error saving credentials:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          description: t('ADMIN_SETTINGS_ERRORS_SAVECREDENTIALS'),
        });
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // For single credentials
    const value = credentialValues[key as keyof typeof credentialValues];
    if (!value) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('ADMIN_SETTINGS_ERRORS_MISSINGVALUE'),
      });
      return;
    }

    setIsSaving(true);
    try {
      await upsertSettingMutation({ key, value });

      const configKey = key as keyof typeof configuredCredentials;
      if (configKey in configuredCredentials) {
        setConfiguredCredentials((prev) => ({ ...prev, [configKey]: true }));
      }
      setEditingCredential(null);
      setCredentialValues((prev) => ({ ...prev, [key]: '' }));
      toast({
        variant: 'success',
        title: 'Success',
        description: t('ADMIN_SETTINGS_TOASTS_CREDENTIALSAVED'),
      });
    } catch (error) {
      console.error('Error saving credential:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: t('ADMIN_SETTINGS_ERRORS_SAVECREDENTIAL'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelEditCredential = () => {
    setEditingCredential(null);
    setCredentialValues({
      blocksProjectKey: '',
      openRouterApiKey: '',
      seliseClientId: '',
      seliseClientSecret: '',
    });
  };

  const confirmRemoveCredential = async () => {
    if (!removingCredential) return;

    setIsSaving(true);
    try {
      // For SELISE credentials, remove both
      if (removingCredential === 'seliseCredentials') {
        await Promise.all([
          upsertSettingMutation({ key: 'seliseClientId', value: '' }),
          upsertSettingMutation({ key: 'seliseClientSecret', value: '' }),
        ]);

        setConfiguredCredentials((prev) => ({
          ...prev,
          seliseCredentials: false,
        }));
      } else {
        await upsertSettingMutation({ key: removingCredential, value: '' });

        const configKey = removingCredential as keyof typeof configuredCredentials;
        if (configKey in configuredCredentials) {
          setConfiguredCredentials((prev) => ({ ...prev, [configKey]: false }));
        }
      }

      setRemovingCredential(null);
      toast({
        variant: 'success',
        title: 'Success',
        description: t('ADMIN_SETTINGS_TOASTS_CREDENTIALREMOVED'),
      });
    } catch (error) {
      console.error('Error removing credential:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description:
          removingCredential === 'seliseCredentials'
            ? t('ADMIN_SETTINGS_ERRORS_REMOVECREDENTIALS')
            : t('ADMIN_SETTINGS_ERRORS_REMOVECREDENTIAL'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getRemovingCredentialLabel = () => {
    switch (removingCredential) {
      case 'blocksProjectKey':
        return t('ADMIN_SETTINGS_INTEGRATION_BLOCKS_LABEL');
      case 'openRouterApiKey':
        return t('ADMIN_SETTINGS_INTEGRATION_OPENROUTER_LABEL');
      case 'seliseCredentials':
        return t('ADMIN_SETTINGS_INTEGRATION_SELISE_LABEL');
      default:
        return t('ADMIN_SETTINGS_COMMON_CREDENTIAL');
    }
  };

  if (isLoading) {
    return (
      <div className="container max-w-4xl py-8 px-4">
        <div className="flex items-center gap-4 mb-8">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const settingsTabs: ReadonlyArray<ContractsTabsListOption<SettingsTabValue>> = [
    { value: 'integration', labelKey: 'ADMIN_SETTINGS_TABS_INTEGRATION' },
    { value: 'document', labelKey: 'ADMIN_SETTINGS_TABS_DOCUMENT' },
    { value: 'enrichment', labelKey: 'ADMIN_SETTINGS_TABS_ENRICHMENT' },
    { value: 'dynamic', labelKey: 'ADMIN_SETTINGS_TABS_DYNAMIC' },
    { value: 'configurator', labelKey: 'ADMIN_SETTINGS_TABS_CONFIGURATOR' },
  ];

  return (
    <div className="space-y-6 md:space-y-8 w-full">
      {/* Header */}
      <div className="space-y-1 md:space-y-2">
        <h1 className="text-2xl md:text-3xl font-bold text-[hsl(var(--fg))] font-heading">
          {t('ADMIN_SETTINGS_TITLE')}
        </h1>
        <p className="text-sm md:text-base text-globe-grey">{t('ADMIN_SETTINGS_DESCRIPTION')}</p>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 md:space-y-6">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as SettingsTabValue)}
          className="w-full space-y-4 md:space-y-5"
        >
          <ContractsTabsList<SettingsTabValue>
            options={settingsTabs}
            activeValue={activeTab}
            onSelect={setActiveTab}
            visibleCount={2}
          />

          <TabsContent value="integration" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-selise-blue/10 flex items-center justify-center">
                    <KeyRound className="h-5 w-5 text-selise-blue" />
                  </div>
                  <div>
                    <CardTitle>{t('ADMIN_SETTINGS_INTEGRATION_TITLE')}</CardTitle>
                    <CardDescription>{t('ADMIN_SETTINGS_INTEGRATION_DESCRIPTION')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* SELISE Blocks Project Key */}
                <div className="space-y-2">
                  <Label>{t('ADMIN_SETTINGS_INTEGRATION_BLOCKS_LABEL')}</Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_INTEGRATION_BLOCKS_DESCRIPTION')}
                  </p>

                  {editingCredential !== 'blocksProjectKey' ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      {configuredCredentials.blocksProjectKey ? (
                        <>
                          <Badge className="bg-lime-green/15 text-poly-green border-[hsl(var(--lime-green))]/30 gap-1.5">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_CONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('blocksProjectKey')}
                            className="gap-1.5"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_ACTIONS_UPDATE')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRemovingCredential('blocksProjectKey')}
                            className="text-crimson hover:text-crimson hover:bg-crimson/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge variant="secondary" className="gap-1.5">
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_NOTCONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('blocksProjectKey')}
                            className="gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_BLOCKS_ACTIONS_ADD')}
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg border border-selise-blue/20 bg-selise-blue/5 space-y-3">
                      <Input
                        type="password"
                        placeholder={t('ADMIN_SETTINGS_INTEGRATION_BLOCKS_PLACEHOLDER')}
                        value={credentialValues.blocksProjectKey}
                        onChange={(e) =>
                          setCredentialValues((prev) => ({
                            ...prev,
                            blocksProjectKey: e.target.value,
                          }))
                        }
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSaveCredential('blocksProjectKey')}
                          disabled={isSaving || !credentialValues.blocksProjectKey}
                        >
                          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                          {t('ADMIN_SETTINGS_ACTIONS_SAVE')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={cancelEditCredential}
                          disabled={isSaving}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          {t('ADMIN_SETTINGS_ACTIONS_CANCEL')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* OpenRouter API Key */}
                <div className="space-y-2">
                  <Label>{t('ADMIN_SETTINGS_INTEGRATION_OPENROUTER_LABEL')}</Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_INTEGRATION_OPENROUTER_DESCRIPTION')}
                  </p>

                  {editingCredential !== 'openRouterApiKey' ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      {configuredCredentials.openRouterApiKey ? (
                        <>
                          <Badge className="bg-lime-green/15 text-poly-green border-[hsl(var(--lime-green))]/30 gap-1.5">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_CONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('openRouterApiKey')}
                            className="gap-1.5"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_ACTIONS_UPDATE')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRemovingCredential('openRouterApiKey')}
                            className="text-crimson hover:text-crimson hover:bg-crimson/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge variant="secondary" className="gap-1.5">
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_NOTCONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('openRouterApiKey')}
                            className="gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_OPENROUTER_ACTIONS_ADD')}
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg border border-selise-blue/20 bg-selise-blue/5 space-y-3">
                      <Input
                        type="password"
                        placeholder={t('ADMIN_SETTINGS_INTEGRATION_OPENROUTER_PLACEHOLDER')}
                        value={credentialValues.openRouterApiKey}
                        onChange={(e) =>
                          setCredentialValues((prev) => ({
                            ...prev,
                            openRouterApiKey: e.target.value,
                          }))
                        }
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSaveCredential('openRouterApiKey')}
                          disabled={isSaving || !credentialValues.openRouterApiKey}
                        >
                          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                          {t('ADMIN_SETTINGS_ACTIONS_SAVE')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={cancelEditCredential}
                          disabled={isSaving}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          {t('ADMIN_SETTINGS_ACTIONS_CANCEL')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* SELISE Signature Credentials (paired) */}
                <div className="space-y-2">
                  <Label>{t('ADMIN_SETTINGS_INTEGRATION_SELISE_LABEL')}</Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_INTEGRATION_SELISE_DESCRIPTION')}
                  </p>

                  {editingCredential !== 'seliseCredentials' ? (
                    <div className="flex items-center gap-3 flex-wrap">
                      {configuredCredentials.seliseCredentials ? (
                        <>
                          <Badge className="bg-lime-green/15 text-poly-green border-[hsl(var(--lime-green))]/30 gap-1.5">
                            <CheckCircle2 className="h-3 w-3" />
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_CONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('seliseCredentials')}
                            className="gap-1.5"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_ACTIONS_UPDATE')}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRemovingCredential('seliseCredentials')}
                            className="text-crimson hover:text-crimson hover:bg-crimson/10"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Badge variant="secondary" className="gap-1.5">
                            {t('ADMIN_SETTINGS_INTEGRATION_STATUS_NOTCONFIGURED')}
                          </Badge>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCredential('seliseCredentials')}
                            className="gap-1.5"
                          >
                            <Plus className="h-3.5 w-3.5" />
                            {t('ADMIN_SETTINGS_INTEGRATION_SELISE_ACTIONS_ADD')}
                          </Button>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="p-4 rounded-lg border border-selise-blue/20 bg-selise-blue/5 space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label htmlFor="seliseClientIdInput" className="text-xs">
                            {t('ADMIN_SETTINGS_INTEGRATION_SELISE_CLIENTIDLABEL')}
                          </Label>
                          <Input
                            id="seliseClientIdInput"
                            type="password"
                            placeholder={t('ADMIN_SETTINGS_INTEGRATION_SELISE_CLIENTIDPLACEHOLDER')}
                            value={credentialValues.seliseClientId}
                            onChange={(e) =>
                              setCredentialValues((prev) => ({
                                ...prev,
                                seliseClientId: e.target.value,
                              }))
                            }
                            autoFocus
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="seliseClientSecretInput" className="text-xs">
                            {t('ADMIN_SETTINGS_INTEGRATION_SELISE_CLIENTSECRETLABEL')}
                          </Label>
                          <Input
                            id="seliseClientSecretInput"
                            type="password"
                            placeholder={t(
                              'ADMIN_SETTINGS_INTEGRATION_SELISE_CLIENTSECRETPLACEHOLDER'
                            )}
                            value={credentialValues.seliseClientSecret}
                            onChange={(e) =>
                              setCredentialValues((prev) => ({
                                ...prev,
                                seliseClientSecret: e.target.value,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSaveCredential('seliseCredentials')}
                          disabled={
                            isSaving ||
                            !credentialValues.seliseClientId ||
                            !credentialValues.seliseClientSecret
                          }
                        >
                          {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                          {t('ADMIN_SETTINGS_ACTIONS_SAVE')}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={cancelEditCredential}
                          disabled={isSaving}
                        >
                          <X className="h-3.5 w-3.5 mr-1" />
                          {t('ADMIN_SETTINGS_ACTIONS_CANCEL')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="document" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-selise-blue" />
                    <div>
                      <CardTitle>{t('ADMIN_SETTINGS_DOCUMENT_TITLE')}</CardTitle>
                      <CardDescription>{t('ADMIN_SETTINGS_DOCUMENT_DESCRIPTION')}</CardDescription>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primaryOutline"
                    size="sm"
                    onClick={handleResetDocumentGeneration}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t('ADMIN_SETTINGS_DOCUMENT_ACTIONS_RESET')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="documentGenerationAiModel">
                    {t('ADMIN_SETTINGS_DOCUMENT_AIMODEL_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_DOCUMENT_AIMODEL_DESCRIPTION')}
                  </p>
                  <div className="max-w-md">
                    <ModelSelector
                      value={
                        form.watch('documentGenerationAiModel') || DEFAULT_DOCUMENT_GENERATION_MODEL
                      }
                      onChange={(value) => form.setValue('documentGenerationAiModel', value)}
                      useCase="documentGeneration"
                      placeholder={t('ADMIN_SETTINGS_COMMON_SELECTAIMODEL')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commonPromptInstructions">
                    {t('ADMIN_SETTINGS_DOCUMENT_COMMONINSTRUCTIONS_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_DOCUMENT_COMMONINSTRUCTIONS_DESCRIPTION')}
                  </p>
                  <textarea
                    id="commonPromptInstructions"
                    placeholder={t('ADMIN_SETTINGS_DOCUMENT_COMMONINSTRUCTIONS_PLACEHOLDER')}
                    className="flex min-h-48 md:min-h-96 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...form.register('commonPromptInstructions')}
                  />
                  {form.watch('commonPromptInstructions') && (
                    <div className="flex justify-between items-center text-xs text-globe-grey">
                      <span>
                        {t('ADMIN_SETTINGS_COMMON_CHARACTERCOUNT', {
                          count: form.watch('commonPromptInstructions')?.length || 0,
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-[hsl(var(--poly-green))]/5">
                  <input
                    type="checkbox"
                    id="documentGenerationOutputInUserLocale"
                    checked={form.watch('documentGenerationOutputInUserLocale')}
                    onChange={(e) =>
                      form.setValue('documentGenerationOutputInUserLocale', e.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="documentGenerationOutputInUserLocale"
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <Globe className="h-4 w-4 text-poly-green" />
                      {t('ADMIN_SETTINGS_DOCUMENT_OUTPUTLOCALE_LABEL')}
                    </Label>
                    <p className="text-xs text-globe-grey mt-1">
                      {t('ADMIN_SETTINGS_DOCUMENT_OUTPUTLOCALE_DESCRIPTION')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="enrichment" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Bot className="h-5 w-5 text-lime-green" />
                    <div>
                      <CardTitle>{t('ADMIN_SETTINGS_ENRICHMENT_TITLE')}</CardTitle>
                      <CardDescription>
                        {t('ADMIN_SETTINGS_ENRICHMENT_DESCRIPTION')}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primaryOutline"
                    size="sm"
                    onClick={() => {
                      form.setValue('formEnrichmentAiModel', DEFAULT_FORM_ENRICHMENT_MODEL);
                      toast({
                        variant: 'info',
                        title: 'Info',
                        description: t('ADMIN_SETTINGS_TOASTS_RESETENRICHMENT'),
                      });
                    }}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t('ADMIN_SETTINGS_ENRICHMENT_ACTIONS_RESET')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="formEnrichmentAiModel">
                    {t('ADMIN_SETTINGS_ENRICHMENT_AIMODEL_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_ENRICHMENT_AIMODEL_DESCRIPTION')}
                  </p>
                  <div className="max-w-md">
                    <ModelSelector
                      value={form.watch('formEnrichmentAiModel') || DEFAULT_FORM_ENRICHMENT_MODEL}
                      onChange={(value) => form.setValue('formEnrichmentAiModel', value)}
                      useCase="formEnrichment"
                      placeholder={t('ADMIN_SETTINGS_COMMON_SELECTAIMODEL')}
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-[hsl(var(--poly-green))]/5">
                  <input
                    type="checkbox"
                    id="formEnrichmentOutputInUserLocale"
                    checked={form.watch('formEnrichmentOutputInUserLocale')}
                    onChange={(e) =>
                      form.setValue('formEnrichmentOutputInUserLocale', e.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="formEnrichmentOutputInUserLocale"
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <Globe className="h-4 w-4 text-poly-green" />
                      {t('ADMIN_SETTINGS_ENRICHMENT_OUTPUTLOCALE_LABEL')}
                    </Label>
                    <p className="text-xs text-globe-grey mt-1">
                      {t('ADMIN_SETTINGS_ENRICHMENT_OUTPUTLOCALE_DESCRIPTION')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="dynamic" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <Workflow className="h-5 w-5 text-selise-blue" />
                    <div>
                      <CardTitle>{t('ADMIN_SETTINGS_DYNAMIC_TITLE')}</CardTitle>
                      <CardDescription>{t('ADMIN_SETTINGS_DYNAMIC_DESCRIPTION')}</CardDescription>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primaryOutline"
                    size="sm"
                    onClick={handleResetDynamicForm}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t('ADMIN_SETTINGS_DYNAMIC_ACTIONS_RESET')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="dynamicFormAiModel">
                    {t('ADMIN_SETTINGS_DYNAMIC_AIMODEL_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_DYNAMIC_AIMODEL_DESCRIPTION')}
                  </p>
                  <div className="max-w-md">
                    <ModelSelector
                      value={form.watch('dynamicFormAiModel') || DEFAULT_DYNAMIC_AI_MODEL}
                      onChange={(value) => form.setValue('dynamicFormAiModel', value)}
                      useCase="dynamicForm"
                      placeholder={t('ADMIN_SETTINGS_COMMON_SELECTAIMODEL')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="dynamicFormSystemPrompt">
                    {t('ADMIN_SETTINGS_DYNAMIC_SYSTEMPROMPT_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_DYNAMIC_SYSTEMPROMPT_DESCRIPTION')}
                  </p>
                  <textarea
                    id="dynamicFormSystemPrompt"
                    placeholder={t('ADMIN_SETTINGS_DYNAMIC_SYSTEMPROMPT_PLACEHOLDER')}
                    className="flex min-h-48 md:min-h-80 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...form.register('dynamicFormSystemPrompt')}
                  />
                  {form.watch('dynamicFormSystemPrompt') && (
                    <div className="flex justify-between items-center text-xs text-globe-grey">
                      <span>
                        {t('ADMIN_SETTINGS_COMMON_CHARACTERCOUNT', {
                          count: form.watch('dynamicFormSystemPrompt')?.length || 0,
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-[hsl(var(--poly-green))]/5">
                  <input
                    type="checkbox"
                    id="dynamicFormOutputInUserLocale"
                    checked={form.watch('dynamicFormOutputInUserLocale')}
                    onChange={(e) =>
                      form.setValue('dynamicFormOutputInUserLocale', e.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="dynamicFormOutputInUserLocale"
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <Globe className="h-4 w-4 text-poly-green" />
                      {t('ADMIN_SETTINGS_DYNAMIC_OUTPUTLOCALE_LABEL')}
                    </Label>
                    <p className="text-xs text-globe-grey mt-1">
                      {t('ADMIN_SETTINGS_DYNAMIC_OUTPUTLOCALE_DESCRIPTION')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="configurator" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-3">
                    <LayoutGrid className="h-5 w-5 text-mauveine" />
                    <div>
                      <CardTitle>{t('ADMIN_SETTINGS_CONFIGURATOR_TITLE')}</CardTitle>
                      <CardDescription>
                        {t('ADMIN_SETTINGS_CONFIGURATOR_DESCRIPTION')}
                      </CardDescription>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primaryOutline"
                    size="sm"
                    onClick={handleResetTemplateConfigurator}
                    className="gap-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    {t('ADMIN_SETTINGS_CONFIGURATOR_ACTIONS_RESET')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="templateConfiguratorAiModel">
                    {t('ADMIN_SETTINGS_CONFIGURATOR_AIMODEL_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_CONFIGURATOR_AIMODEL_DESCRIPTION')}
                  </p>
                  <div className="max-w-md">
                    <ModelSelector
                      value={
                        form.watch('templateConfiguratorAiModel') ||
                        DEFAULT_TEMPLATE_CONFIGURATOR_MODEL
                      }
                      onChange={(value) => form.setValue('templateConfiguratorAiModel', value)}
                      useCase="documentGeneration"
                      placeholder={t('ADMIN_SETTINGS_COMMON_SELECTAIMODEL')}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="templateConfiguratorBusinessLogic">
                    {t('ADMIN_SETTINGS_CONFIGURATOR_BUSINESSLOGIC_LABEL')}
                  </Label>
                  <p className="text-xs text-globe-grey">
                    {t('ADMIN_SETTINGS_CONFIGURATOR_BUSINESSLOGIC_DESCRIPTION')}
                  </p>
                  <textarea
                    id="templateConfiguratorBusinessLogic"
                    placeholder={t('ADMIN_SETTINGS_CONFIGURATOR_BUSINESSLOGIC_PLACEHOLDER')}
                    className="flex min-h-40 md:min-h-64 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    {...form.register('templateConfiguratorBusinessLogic')}
                  />
                  {form.watch('templateConfiguratorBusinessLogic') && (
                    <div className="flex justify-between items-center text-xs text-globe-grey">
                      <span>
                        {t('ADMIN_SETTINGS_COMMON_CHARACTERCOUNT', {
                          count: form.watch('templateConfiguratorBusinessLogic')?.length || 0,
                        })}
                      </span>
                    </div>
                  )}
                </div>

                <div className="p-4 rounded-lg border border-[hsl(var(--mauveine))]/20 bg-[hsl(var(--mauveine))]/5">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="h-4 w-4 text-mauveine mt-0.5 shrink-0" />
                    <div className="text-xs text-globe-grey">
                      <p className="font-medium text-[hsl(var(--fg))] mb-1">
                        {t('ADMIN_SETTINGS_CONFIGURATOR_INFO_TITLE')}
                      </p>
                      <p>{t('ADMIN_SETTINGS_CONFIGURATOR_INFO_DESCRIPTION')}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="flex  flex-row items-center justify-end gap-2 sm:gap-4 pt-2">
          <Button type="button" variant="outline" size="sm" asChild className="sm:w-auto">
            <Link to={`/admin`}>{t('ADMIN_SETTINGS_ACTIONS_CANCEL')}</Link>
          </Button>
          <Button type="submit" size="sm" disabled={isSaving} className="w-1/2 sm:w-auto">
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            {t('ADMIN_SETTINGS_ACTIONS_SAVESETTINGS')}
          </Button>
        </div>
      </form>

      {/* Confirmation dialog for removing credentials */}
      <AlertDialog open={!!removingCredential} onOpenChange={() => setRemovingCredential(null)}>
        <AlertDialogContent className="w-[95vw] max-w-md max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base sm:text-lg">
              {t('ADMIN_SETTINGS_DIALOG_REMOVETITLE', {
                credential: getRemovingCredentialLabel(),
              })}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs sm:text-sm">
              {t('ADMIN_SETTINGS_DIALOG_REMOVEDESCRIPTION')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel disabled={isSaving} className="w-full sm:w-auto mt-0">
              {t('ADMIN_SETTINGS_ACTIONS_CANCEL')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveCredential}
              disabled={isSaving}
              className="w-full sm:w-auto bg-crimson hover:bg-crimson/90 text-white"
            >
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('ADMIN_SETTINGS_ACTIONS_REMOVE')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
